import axios from 'axios';
import SeriesSvc from '@/services/SeriesSvc';
import { browserUtcOffsetHours } from '@/util/apiDatetime';
import { buildChunkRanges, maxBucketsFor } from '@/util/seriesToSummaryData';
import {
  ROWS_PER_BUCKET, DEFAULT_CHANNELS, channelModeOf, channelsFromRows, requestParamsFor,
} from '@/util/customChartCatalog';

const INTERVAL_SECONDS = { minute: 60, hour: 3600, day: 86400 };
// Batas satu HALAMAN, bukan per kartu: banyak request barengan pernah bikin backend 504.
const MAX_PARALLEL = 4;

let active = 0;
const waiting = [];

function acquire() {
  if (active < MAX_PARALLEL) {
    active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => waiting.push(resolve));
}

function release() {
  const next = waiting.shift();
  if (next) next();
  else active -= 1;
}

async function limited(task) {
  await acquire();
  try {
    return await task();
  } finally {
    release();
  }
}

// Objek error axios bawa header autentikasi, jadi jangan di-log utuh.
function warn(message, e) {
  console.warn(message, e && e.message, e && e.response ? e.response.status : '');
}

function paramsFor(deviceType, deviceUid, interval) {
  return {
    params: {
      device: [deviceUid],
      aggregatedUnit: String(interval).toUpperCase(),
      timezone: browserUtcOffsetHours(),
      ...requestParamsFor(deviceType),
    },
  };
}

async function fetchRequest(req, interval, isStale) {
  const rows = [];
  const step = INTERVAL_SECONDS[interval] || INTERVAL_SECONDS.hour;
  const chunks = buildChunkRanges(req.startSec, req.endSec, maxBucketsFor(ROWS_PER_BUCKET[req.deviceType] || 1) * step);
  for (let i = 0; i < chunks.length; i += 1) {
    if (isStale()) return { rows: [], status: 'cancelled' };
    try {
      // Sengaja berurutan: potongan paralel pernah bikin backend 504.
      // eslint-disable-next-line no-await-in-loop
      const res = await SeriesSvc.getAggregatedDataByPeriod(
        req.deviceType, chunks[i].start, chunks[i].end, paramsFor(req.deviceType, req.deviceUid, interval),
      );
      const body = (res && res.data && res.data.data) || {};
      rows.push(...(body.series || []));
      // Kena batas = datanya kepotong diam-diam; lebih baik dibuang.
      if (body.limit && body.count >= body.limit) return { rows: [], status: 'truncated' };
    } catch (e) {
      if (axios.isCancel(e)) return { rows: [], status: 'cancelled' };
      warn(`[custom chart] ${req.deviceType} ${req.deviceUid} gagal`, e);
      return { rows: [], status: 'failed' };
    }
  }
  return { rows, status: 'ok' };
}

export async function fetchChartRows(plan, interval, isStale = () => false) {
  const rowsByKey = {};
  const statusByKey = {};
  await Promise.all(plan.map((req) => limited(async () => {
    const result = isStale() ? { rows: [], status: 'cancelled' } : await fetchRequest(req, interval, isStale);
    rowsByKey[req.key] = result.rows;
    statusByKey[req.key] = result.status;
  })));
  return { rowsByKey, statusByKey };
}

const channelCache = new Map();

// Posisi mesin / nomor AE yang beneran ngirim data seminggu terakhir.
export function discoverChannels(deviceType, deviceUid, nowMs) {
  const mode = channelModeOf(deviceType);
  if (!mode) return Promise.resolve({ channels: [], recent: true });
  const cacheKey = `${deviceType}|${deviceUid}`;
  if (channelCache.has(cacheKey)) return channelCache.get(cacheKey);
  const endSec = Math.floor(nowMs / 1000);
  const startSec = endSec - 7 * 86400;
  const job = limited(async () => {
    try {
      const res = await SeriesSvc.getAggregatedDataByPeriod(deviceType, startSec, endSec, paramsFor(deviceType, deviceUid, 'day'));
      const rows = (res && res.data && res.data.data && res.data.data.series) || [];
      const found = channelsFromRows(deviceType, rows);
      if (found.length) return { channels: found, recent: true };
    } catch (e) {
      // Gagal jaringan jangan di-cache; coba lagi di pemilihan berikutnya.
      channelCache.delete(cacheKey);
      if (!axios.isCancel(e)) warn('[custom chart] cek channel gagal', e);
    }
    return { channels: DEFAULT_CHANNELS[mode].slice(), recent: false };
  });
  channelCache.set(cacheKey, job);
  return job;
}
